import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Divider,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  Chip,
  Button,
  Card,
  CardContent,
  Tooltip,
  Badge,
  Fade,
  Slide,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { baseClient } from '../api/baseClient';
import {
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';

interface ChatMessage {
  author: 'user' | 'ai';
  text: string;
  timestamp?: Date;
  type?: 'message' | 'insight' | 'action' | 'suggestion';
  actions?: ChatAction[];
}

interface ChatAction {
  label: string;
  action: () => void;
  icon?: React.ReactNode;
}

interface QuickAction {
  label: string;
  prompt: string;
  icon: React.ReactNode;
  category: string;
}

interface ChatWindowProps {
  onClose: () => void;
}

const EnhancedChatWindow: React.FC<ChatWindowProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      author: 'ai',
      text: 'Hello! I\'m your intelligent CRM assistant. I can help you with deals, contacts, accounts, and provide insights. What would you like to know?',
      timestamp: new Date(),
      type: 'message'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showQuickActions, setShowQuickActions] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Quick actions for common queries
  const quickActions: QuickAction[] = [
    {
      label: 'Show my top deals',
      prompt: 'Show me my top 5 deals by value that are currently in progress',
      icon: <TrendingUpIcon />,
      category: 'Deals'
    },
    {
      label: 'Recent contacts',
      prompt: 'Who are the contacts I\'ve interacted with most recently?',
      icon: <PeopleIcon />,
      category: 'Contacts'
    },
    {
      label: 'Account overview',
      prompt: 'Give me an overview of my key accounts and their status',
      icon: <BusinessIcon />,
      category: 'Accounts'
    },
    {
      label: 'Pipeline value',
      prompt: 'What\'s the total value of deals in my pipeline?',
      icon: <AttachMoneyIcon />,
      category: 'Analytics'
    },
    {
      label: 'Today\'s tasks',
      prompt: 'What tasks do I need to complete today?',
      icon: <ScheduleIcon />,
      category: 'Tasks'
    },
    {
      label: 'AI insights',
      prompt: 'Give me AI-powered insights about my sales performance',
      icon: <LightbulbIcon />,
      category: 'Insights'
    }
  ];

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input field when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSendMessage = async (messageText?: string) => {
    const messageToSend = messageText || input.trim();
    if (!messageToSend) return;

    const userMessage: ChatMessage = {
      author: 'user',
      text: messageToSend,
      timestamp: new Date(),
      type: 'message'
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShowQuickActions(false);

    try {
      // Call the CRM Assistant API
      const response = await baseClient.post('/assistant/chat', {
        message: messageToSend,
        conversation_id: conversationId
      });

      // Update conversation ID if provided
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Create AI response message
      const aiMessage: ChatMessage = {
        author: 'ai',
        text: response.message.text,
        timestamp: new Date(response.message.timestamp),
        type: response.message.message_type || 'message',
        actions: response.suggested_actions?.map((action: any) => ({
          label: action.label,
          action: () => {
            // Navigate to appropriate pages instead of sending messages
            const actionMap: Record<string, string> = {
              'search_accounts': '/accounts',
              'search_contacts': '/contacts',
              'search_deals': '/deals',
              'search_crm': '/',
              'create_task': '/tasks',
              'view_tasks': '/tasks',
              'view_overdue': '/tasks',
              'view_deals': '/deals',
              'create_deal': '/deals',
              'view_analytics': '/analytics',
              'view_dashboard': '/dashboard',
              'view_sales': '/analytics',
              'view_performance': '/performance',
              'create_contact': '/contacts',
              'view_contacts': '/contacts',
              'create_account': '/accounts',
              'view_accounts': '/accounts'
            };
            
            const path = actionMap[action.action];
            if (path) {
              navigate(path);
              onClose(); // Close chat after navigation
            } else {
              console.log('Unknown action:', action.action);
            }
          }
        }))
      };

      setMessages(prev => [...prev, aiMessage]);

      // Text-to-speech for AI response (if enabled)
      if (isSpeaking && response.message.text) {
        speakText(response.message.text);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.author === 'ai') {
          lastMessage.text = 'I apologize, but I encountered an error processing your request. Please try again.';
          lastMessage.type = 'message';
        }
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateActionsFromResponse = (response: string): ChatAction[] => {
    const actions: ChatAction[] = [];

    // Look for specific patterns in response to suggest actions
    if (response.toLowerCase().includes('deal') || response.toLowerCase().includes('opportunity')) {
      actions.push({
        label: 'View Deals',
        action: () => window.location.href = '/deals',
        icon: <TrendingUpIcon />
      });
    }

    if (response.toLowerCase().includes('contact') || response.toLowerCase().includes('person')) {
      actions.push({
        label: 'View Contacts',
        action: () => window.location.href = '/contacts',
        icon: <PeopleIcon />
      });
    }

    if (response.toLowerCase().includes('account') || response.toLowerCase().includes('company')) {
      actions.push({
        label: 'View Accounts',
        action: () => window.location.href = '/accounts',
        icon: <BusinessIcon />
      });
    }

    return actions;
  };

  const handleQuickAction = (action: QuickAction) => {
    handleSendMessage(action.prompt);
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    }
  };

  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      // Cancel any in-flight utterance so the new one isn't queued behind a silent/quiet voice
      try { speechSynthesis.cancel(); } catch { /* noop */ }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 1; // Force max volume; some browsers default <1 for synthesized speech
      // Prefer a louder/clearer English voice when available
      try {
        const voices = speechSynthesis.getVoices();
        const preferred = voices.find(v => /en[-_](US|GB)/i.test(v.lang) && /Google|Microsoft|Samantha|Daniel/i.test(v.name))
          || voices.find(v => /^en/i.test(v.lang));
        if (preferred) utterance.voice = preferred;
      } catch { /* noop */ }
      speechSynthesis.speak(utterance);
    }
  };

  const clearConversation = () => {
    setMessages([
      {
        author: 'ai',
        text: 'Conversation cleared. How can I help you?',
        timestamp: new Date(),
        type: 'message'
      }
    ]);
    setConversationId(null);
    setShowQuickActions(true);
    setMenuAnchor(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Paper
      elevation={8}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
            <SmartToyIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              CRM Assistant
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              AI-powered CRM helper
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={isSpeaking ? "Disable speech" : "Enable speech"}>
            <IconButton
              onClick={() => setIsSpeaking(!isSpeaking)}
              sx={{ color: 'white' }}
            >
              {isSpeaking ? <VolumeUpIcon /> : <VolumeOffIcon />}
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ color: 'white' }}
          >
            <MoreVertIcon />
          </IconButton>

          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={clearConversation}>
          <ListItemIcon>
            <RefreshIcon />
          </ListItemIcon>
          <ListItemText primary="Clear Conversation" />
        </MenuItem>
      </Menu>

      {/* Messages Area */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        p: 1,
        background: 'rgba(255, 255, 255, 0.95)',
        color: 'black'
      }}>
        <List>
          {messages.map((message, index) => (
            <Fade in={true} key={index}>
              <ListItem sx={{
                display: 'flex',
                justifyContent: message.author === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}>
                <Card
                  sx={{
                    maxWidth: '80%',
                    bgcolor: message.author === 'user'
                      ? 'primary.main'
                      : message.type === 'insight'
                        ? 'warning.light'
                        : 'grey.100',
                    color: message.author === 'user' ? 'white' : 'black'
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: message.author === 'user' ? 'rgba(255,255,255,0.2)' : 'primary.main'
                        }}
                      >
                        {message.author === 'user' ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                      </Avatar>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {message.author === 'user' ? 'You' : 'Assistant'}
                        {message.timestamp && ` • ${message.timestamp.toLocaleTimeString()}`}
                      </Typography>
                      {message.type === 'insight' && (
                        <Chip
                          label="Insight"
                          size="small"
                          color="warning"
                          icon={<LightbulbIcon />}
                        />
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.text}
                    </Typography>

                    {message.actions && message.actions.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        {message.actions.map((action, actionIndex) => (
                          <Button
                            key={actionIndex}
                            size="small"
                            variant="outlined"
                            startIcon={action.icon}
                            onClick={action.action}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </ListItem>
            </Fade>
          ))}

          {isLoading && (
            <ListItem>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Assistant is thinking...
                </Typography>
              </Box>
            </ListItem>
          )}
        </List>
        <div ref={messagesEndRef} />
      </Box>

      {/* Quick Actions */}
      {showQuickActions && messages.length <= 1 && (
        <Slide direction="up" in={showQuickActions}>
          <Box sx={{ p: 2, background: 'rgba(255, 255, 255, 0.95)' }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickActions.map((action, index) => (
                <Chip
                  key={index}
                  label={action.label}
                  icon={action.icon}
                  onClick={() => handleQuickAction(action)}
                  clickable
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Box>
        </Slide>
      )}

      <Divider />

      {/* Input Area */}
      <Box sx={{
        p: 2,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <TextField
          ref={inputRef}
          fullWidth
          variant="outlined"
          placeholder="Ask me anything about your CRM data..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          multiline
          maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'white' }
            }
          }}
        />

        <Tooltip title="Voice input">
          <span>
            <IconButton
              onClick={startListening}
              disabled={isLoading || isListening}
              sx={{
                color: 'white',
                bgcolor: isListening ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
              }}
            >
              {isListening ? <MicIcon /> : <MicOffIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Send message">
          <span>
            <IconButton
              onClick={() => handleSendMessage()}
              disabled={isLoading || !input.trim()}
              sx={{
                color: 'white',
                bgcolor: input.trim() ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default EnhancedChatWindow;